class SizeDrawer extends HTMLElement {
  constructor() {
    super();
    this.handleOpenButtonClick = this.handleOpenButtonClick.bind(this);
    this.handleTableHover = this.handleTableHover.bind(this);
    this.handleTableLeave = this.handleTableLeave.bind(this);
    this.handleTableClick = this.handleTableClick.bind(this);
    this.initSelectedSize = this.initSelectedSize.bind(this);

    // Close button listener
    this.querySelector('[id^="ModalClose-"]')?.addEventListener('click', this.hide.bind(this, false));

    // ESC key listener
    this.addEventListener('keyup', (event) => {
      if (event.code && event.code.toUpperCase() === 'ESCAPE') this.hide();
    });

    // Backdrop click listener (click on the drawer itself, not its content)
    this.addEventListener('click', (event) => {
      if (event.target === this) this.hide();
    });
  }

  connectedCallback() {
    // Move to body if not already there
    if (this.moved) return;
    this.moved = true;
    document.body.appendChild(this);

    // Listen for open button click
    this.openButtons = document.querySelectorAll('#open-size-drawer, .js-open-size-drawer');
    this.actionButton = this.querySelector('.size-guide--actions > button');
    this.BISOpen = this.querySelector('.size-guide--actions > modal-opener[data-modal="#Drawer-BackInStock"]');

    if (this.BISOpen) {
      this.BISOpen.addEventListener('click', this.hide.bind(this));
    }
    if (this.actionButton) {
      this.actionButton.addEventListener('click', async () => {
        this.hide();
        const B_VARIANT_CLASS = 'b-size-popup';
        const isBVariant = () => document.body.classList.contains(B_VARIANT_CLASS);
        if (isBVariant()) {
          const pf = document.querySelector('product-form');
          if (pf) pf.dataset.skipSizeModalOnce = 'true';
        }
        document.querySelector('.product-form__submit')?.click();
      });
    }
    if (this.openButtons.length > 0) {
      this.openButtons.forEach(button => button.addEventListener('click', this.handleOpenButtonClick));
    }
    // Table hover logic
    this.initTableHover();
    // Table click logic
    this.initTableClick();
    // Listen for size-tabs radio change
    this.initSizeTabs();
  }

  handleOpenButtonClick(event) {
    event.preventDefault();
    this.show(event.currentTarget);
  }

  show(opener) {
    this.openedBy = opener;
    document.body.classList.add('overflow-hidden');
    this.setAttribute('open', '');

    // Focus the dialog
    trapFocus(this, this.querySelector('[role="dialog"]'));
    window.pauseAllMedia();

    const table = this.querySelector('.size-guide--table');
    const isRegionTabs = table?.dataset.regionTabs === 'true';

    if (isRegionTabs) {
      // Region mode (EU/UK/US): select tab by country, show correct tbody, set units
      const region = this.getRegionFromCountry();
      this.setRegionTab(region);
      this.showRegionTbody(region);
      const unit = region === 'us' ? 'in' : 'cm';
      this.updateTableUnits(unit);
      this.updateTableHeadings(unit);
    } else {
      // Legacy mode: reset to cm if measurement switcher exists
      const hasSwitcher = this.querySelector('.size-guide--tabs');
      if (hasSwitcher) {
        this.updateTableUnits('cm');
        const cmRadio = this.querySelector('input[name="size-tabs"][value="cm"]');
        if (cmRadio) cmRadio.checked = true;
      }
    }

    // Check and add size guide property input if needed, set name for open state
    this.ensureSizeGuideInput();
    this.setSizeGuideInputName('properties[_sizeguide]');

    this.initSelectedSize();
  }

  /** Map country code to size region: eu, uk, or us */
  getRegionFromCountry() {
    const code = (this.dataset.countryCode || document.querySelector('input[name="country_code"]')?.value || '').toUpperCase();
    if (code === 'GB' || code === 'UK') return 'uk';
    if (code === 'US') return 'us';
    return 'eu';
  }

  setRegionTab(region) {
    const radio = this.querySelector(`input[name="size-tabs-region"][value="${region}"]`);
    if (radio) radio.checked = true;
  }

  showRegionTbody(region) {
    const tbodies = this.querySelectorAll('.size-guide--tbody-region');
    tbodies.forEach((tbody) => {
      const isActive = tbody.dataset.region === region;
      tbody.hidden = !isActive;
    });
  }

  hide() {
    document.body.classList.remove('overflow-hidden');
    document.body.dispatchEvent(new CustomEvent('modalClosed'));
    this.removeAttribute('open');
    removeTrapFocus(this.openedBy);
    window.pauseAllMedia();

    // Change size guide input name after 0.5 second delay
    setTimeout(() => {
      this.setSizeGuideInputName('properties[_sizeguide_PDP]');
    }, 500);
  }

  ensureSizeGuideInput() {
    // Find the product form
    const cartForm = document.querySelector('.product-form form');
    if (!cartForm) return;

    // Check if input with either name already exists
    const existingInput = cartForm.querySelector('input[name="properties[_sizeguide]"], input[name="properties[_sizeguide_PDP]"]');
    if (existingInput) return;

    // Create and add the input if it doesn't exist (default to PDP name)
    const sizeGuideInput = document.createElement('input');
    sizeGuideInput.type = 'hidden';
    sizeGuideInput.name = 'properties[_sizeguide_PDP]';
    sizeGuideInput.value = 'true';

    cartForm.appendChild(sizeGuideInput);
  }

  setSizeGuideInputName(newName) {
    // Find the product form
    const cartForm = document.querySelector('.product-form form');
    if (!cartForm) return;

    // Find the size guide input with either possible name
    const sizeGuideInput = cartForm.querySelector('input[name="properties[_sizeguide]"], input[name="properties[_sizeguide_PDP]"]');
    if (sizeGuideInput) {
      sizeGuideInput.name = newName;
    }
  }

  initSelectedSize() {
    // Try multiple option names (Size, Größe, etc.)
    const optionNames = ['Size', 'Größe', 'Taille', 'Talla', 'Tamanho'];
    let selectedSize = null;

    for (const optionName of optionNames) {
      selectedSize = document.querySelector(`variant-selects input[name="${optionName}"]:checked`);
      if (selectedSize) break;
    }

    if (selectedSize) {
      const targetCell = this.querySelector(`td[data-size="${selectedSize.value}"] + td`);
      if (targetCell) {
        targetCell.dispatchEvent(new Event('click', { bubbles: true }));
      }
    }
  }

  // --- Table hover logic ---
  initTableHover() {
    this.sizeGuideTable = this.querySelector('.size-guide--table table');
    if (!this.sizeGuideTable) return;
    this.tableCells = this.sizeGuideTable.querySelectorAll('th, td');
    this.tableCells.forEach(cell => {
      cell.addEventListener('mouseenter', this.handleTableHover);
      cell.addEventListener('mouseleave', this.handleTableLeave);
    });
  }

  cleanupTableHover() {
    if (!this.tableCells) return;
    this.tableCells.forEach(cell => {
      cell.removeEventListener('mouseenter', this.handleTableHover);
      cell.removeEventListener('mouseleave', this.handleTableLeave);
    });
  }

  handleTableHover(e) {
    const cell = e.currentTarget;
    const table = this.sizeGuideTable;
    if (!table) return;
    // Find cell index and row
    const row = cell.parentElement;
    const cellIndex = Array.from(cell.parentElement.children).indexOf(cell);
    // Highlight all cells in the same row
    if (cell.tagName !== 'TH') {
      Array.from(row.children).forEach(c => c.classList.add('highlight'));
    }
    // Highlight all cells in the same column
    if (!cell.getAttribute('data-size')) {
      Array.from(table.rows).forEach(r => {
        if (r.children[cellIndex]) {
          r.children[cellIndex].classList.add('highlight');
        }
      });
    }
  }

  handleTableLeave(e) {
    // Remove all highlights
    if (!this.sizeGuideTable) return;
    this.sizeGuideTable.querySelectorAll('.highlight').forEach(cell => cell.classList.remove('highlight'));
  }

  // --- Table click logic ---
  initTableClick() {
    if (!this.sizeGuideTable) return;
    this.tableTds = this.sizeGuideTable.querySelectorAll('td');
    this.tableTds.forEach(td => {
      td.addEventListener('click', this.handleTableClick);
    });
  }

  cleanupTableClick() {
    if (!this.tableTds) return;
    this.tableTds.forEach(td => {
      td.removeEventListener('click', this.handleTableClick);
    });
  }

  handleTableClick(e) {
    // Remove 'selected' from all tds
    this.tableTds.forEach(td => td.classList.remove('selected'));
    // Add 'selected' to clicked td
    e.currentTarget.classList.add('selected');
    // Enable/disable the action button
    this.toggleActionButton();
    // Select the corresponding variant radio input
    this.selectVariantInputForSelectedRow(e.currentTarget);
  }

  selectVariantInputForSelectedRow(selectedTd) {
    // Find the row (tr) of the selected td
    const row = selectedTd.closest('tr');
    if (!row) return;
    // Get the first td's text (size label)
    const sizeLabel = row.querySelector('td')?.textContent?.trim();
    if (!sizeLabel) return;

    // Find the input in variant-selects - try multiple option names
    const variantSelects = document.querySelector('variant-selects');
    if (!variantSelects) return;

    const optionNames = ['Size', 'Größe', 'Taille', 'Talla', 'Tamanho'];
    let sizeInput = null;

    for (const optionName of optionNames) {
      sizeInput = variantSelects.querySelector(`input[name='${optionName}'][value='${CSS.escape(sizeLabel)}']`);
      if (sizeInput) break;
    }

    if (sizeInput) {
      sizeInput.checked = true;
      sizeInput.dispatchEvent(new Event('change', { bubbles: true }));
      if (this.actionButton && this.actionButton.classList.contains('hidden')) {
        this.actionButton.classList.remove('hidden');
      }
      if (this.BISOpen && !this.BISOpen.classList.contains('hidden')) {
        this.BISOpen.classList.add('hidden');
      }
    }
    if (sizeInput && sizeInput.dataset.disabled) {
      if (this.actionButton && !this.actionButton.classList.contains('hidden')) {
        this.actionButton.classList.add('hidden');
      }
      if (this.BISOpen && this.BISOpen.classList.contains('hidden')) {
        this.BISOpen.classList.remove('hidden');
      }
    }
  }

  toggleActionButton() {
    const hasSelected = Array.from(this.tableTds).some(td => td.classList.contains('selected'));
    if (this.actionButton) {
      this.actionButton.disabled = !hasSelected;
    }
  }

  initSizeTabs() {
    const regionTabInputs = this.querySelectorAll('input[name="size-tabs-region"]');
    const legacyTabInputs = this.querySelectorAll('input[name="size-tabs"]');

    if (regionTabInputs.length > 0) {
      // Region mode: EU / UK / US
      regionTabInputs.forEach((input) => {
        input.addEventListener('change', this.handleRegionTabChange.bind(this));
      });
      const region = this.getRegionFromCountry();
      this.setRegionTab(region);
      this.showRegionTbody(region);
      const unit = region === 'us' ? 'in' : 'cm';
      this.updateTableUnits(unit);
      this.updateTableHeadings(unit);
      return;
    }

    if (legacyTabInputs.length === 0) return;

    // Legacy mode: CM / Inches
    legacyTabInputs.forEach((input) => {
      input.addEventListener('change', this.handleSizeTabChange.bind(this));
    });
    this.updateTableUnits('cm');
  }

  handleRegionTabChange(e) {
    const region = e.target.value;
    this.showRegionTbody(region);
    const unit = region === 'us' ? 'in' : 'cm';
    this.updateTableUnits(unit);
    this.updateTableHeadings(unit);
  }

  handleSizeTabChange(e) {
    const unit = e.target.value;
    this.updateTableUnits(unit);
  }

  updateTableUnits(unit) {
    const table = this.querySelector('.size-guide--table');
    if (!table) return;

    let buttons;
    if (table.dataset.regionTabs === 'true') {
      const visibleTbody = table.querySelector('.size-guide--tbody-region:not([hidden])');
      buttons = visibleTbody ? visibleTbody.querySelectorAll('td > button[data-cm]') : [];
    } else {
      buttons = this.querySelectorAll('.size-guide--table td > button[data-cm]');
    }

    buttons.forEach((btn) => {
      const cmValue = btn.getAttribute('data-cm');
      if (!cmValue) return;
      if (unit === 'cm') {
        btn.textContent = cmValue;
      } else if (unit === 'in') {
        const num = parseFloat(cmValue);
        if (!isNaN(num)) {
          btn.textContent = (num * 0.3937007874).toFixed(2);
        } else {
          btn.textContent = cmValue;
        }
      }
    });
  }

  /**
   * In region-tabs mode: switch table header wording between cm (EU) and inch (UK/US).
   */
  updateTableHeadings(unit) {
    const table = this.querySelector('.size-guide--table');
    if (!table || table.dataset.regionTabs !== 'true') return;

    const headers = table.querySelectorAll('thead th');
    headers.forEach((th) => {
      if (!th.dataset.headingCm) {
        th.dataset.headingCm = th.textContent.trim();
      }
      const base = th.dataset.headingCm;
      if (unit === 'cm') {
        th.textContent = base;
      } else {
        th.textContent = base.replace(/\bcm\b/gi, 'inch');
      }
    });
  }
}

document.addEventListener('DOMContentLoaded', () => {
  customElements.define('size-drawer', SizeDrawer);
});
